import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { findOrCreateVisitor, updateVisitorNdaSigned } from "./fibery";
import { NDA_CONTENT } from "./NdaContent";
import "./VisitorSignIn.css";

// Aborts a fetch if it hasn't resolved within timeoutMs, so a stalled
// downstream service (SMTP relay, Fibery file API) can't hang the button.
async function fetchWithTimeout(url, options, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

function VisitorSignIn() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1=form, 2=nda
    const [formData, setFormData] = useState({
        firstName: "",
        surname: "",
        email: "",
        phoneNumber: "",
    });
    const [signature, setSignature] = useState(""); // Editable signature
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [visitorData, setVisitorData] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const uniFiToken = process.env.REACT_APP_UNIFI_TOKEN || "";
            console.log(
                "UniFi Token from env:",
                uniFiToken ? "Present" : "Missing",
            );
            console.log("Token value:", uniFiToken);

            const visitor = await findOrCreateVisitor(
                formData.firstName,
                formData.surname,
                formData.email,
                formData.phoneNumber,
                uniFiToken,
            );

            console.log("Visitor found/created:", visitor);

            if (visitor.isNew) {
                console.log("New visitor created with ID:", visitor.id);
            } else {
                console.log("Existing visitor found with ID:", visitor.id);
            }

            console.log("Visit created:", visitor.visit);
            console.log("NDA Signed:", visitor.ndaSigned);

            setVisitorData(visitor);

            // Check if NDA is signed
            if (!visitor.ndaSigned) {
                // Move to NDA signing step
                console.log("NDA not signed - moving to NDA signing step");
                // Initialize signature with visitor's full name
                setSignature(
                    `${formData.firstName.trim()} ${formData.surname.trim()}`,
                );
                setStep(2);
            } else {
                // NDA already signed - go directly to success
                console.log("NDA already signed - going to success page");
                navigate("/success");
            }
        } catch (err) {
            console.error("Error processing visitor:", err);
            setError(err.message || "An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (step === 2) {
            // Go back to form
            setStep(1);
            setError("");
        } else {
            // Go back to wherever the user came from
            navigate(-1);
        }
    };

    const getCurrentDate = () => {
        const today = new Date();
        return today.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const handleNdaSign = async () => {
        if (!visitorData) {
            setError("Visitor data not found");
            return;
        }

        if (!signature.trim()) {
            setError("Please enter a signature");
            return;
        }

        setError("");
        setLoading(true);

        try {
            const fullName = signature.trim();

            // Create a temporary container to render the NDA with signature
            const tempContainer = document.createElement("div");
            tempContainer.style.position = "absolute";
            tempContainer.style.left = "-10000px";
            tempContainer.style.width = "8.5in";
            tempContainer.style.padding = "20px";
            tempContainer.style.backgroundColor = "white";
            tempContainer.style.fontFamily = "Arial, sans-serif";
            tempContainer.style.fontSize = "11px";
            tempContainer.style.lineHeight = "1.5";
            tempContainer.style.color = "#000";

            const currentDate = getCurrentDate();
            // Replace placeholders with visitor info
            let ndaContent = NDA_CONTENT.replace(/\[DATE\]/g, currentDate)
                .replace(/\[NAME\]/g, fullName)
                .replace(/\[SIG\]/g, fullName);

            // Convert markdown to simple HTML
            const htmlContent = ndaContent
                .split("\n")
                .map((line) => {
                    // Skip lines starting with "Date:"
                    if (line.trim().startsWith("Date:")) {
                        return "";
                    } else if (line.startsWith("**") && line.endsWith("**")) {
                        // Bold headings
                        return `<p style="font-weight: bold; margin-top: 12px; margin-bottom: 8px;">${line
                            .replace(/\*\*/g, "")
                            .trim()}</p>`;
                    } else if (line.match(/^\d+\./)) {
                        // Numbered sections
                        return `<p style="margin-top: 8px; margin-bottom: 8px;">${line}</p>`;
                    } else if (line.trim() === "") {
                        return "";
                    } else if (
                        line.includes("Signature:") &&
                        line.includes(fullName)
                    ) {
                        // Render signature in Caveat font
                        return `<p style="margin: 4px 0; margin-top: 20px;">Signature: <span style="font-family: 'Caveat', cursive; font-size: 28px; font-weight: 700; display: inline-block;">${fullName}</span></p>`;
                    } else {
                        return `<p style="margin: 4px 0;">${line}</p>`;
                    }
                })
                .join("");

            tempContainer.innerHTML = `
        <div>
          ${htmlContent}
        </div>
      `;

            document.body.appendChild(tempContainer);

            // Convert to canvas and then PDF
            const canvas = await html2canvas(tempContainer, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff",
                allowTaint: true,
            });

            document.body.removeChild(tempContainer);

            // JPEG instead of PNG: the rasterized NDA page is mostly flat
            // white background, which JPEG's lossy compression crushes down
            // to a fraction of PNG's lossless size (this is what was making
            // the PDF ~13MB and slowing down every network leg that sends
            // or receives it). compress:true additionally lets jsPDF deflate
            // the PDF's internal object streams.
            const imgData = canvas.toDataURL("image/jpeg", 0.85);
            const pdf = new jsPDF({
                orientation: "p",
                unit: "mm",
                format: "a4",
                compress: true,
            });

            const imgWidth = 210; // A4 width in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
            heightLeft -= 297; // A4 height in mm

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
                heightLeft -= 297;
            }

            // Get PDF as blob - convert to arrayBuffer for reusability
            const pdfArrayBuffer = pdf.output("arraybuffer");
            const pdfBlob = new Blob([pdfArrayBuffer], {
                type: "application/pdf",
            });

            // Send PDF to email via proxy
            const formattedDate = currentDate.replace(/\s+/g, "_");
            const params = new URLSearchParams({
                firstName: formData.firstName.trim(),
                surname: formData.surname.trim(),
                email: formData.email.trim(),
                date: formattedDate,
            });

            // Create a new blob from the arrayBuffer for email (to avoid blob reuse issues)
            const emailBlob = new Blob([pdfArrayBuffer], {
                type: "application/pdf",
            });

            // The email send is the only step the visitor needs to wait on.
            // The Fibery ndaSigned update and PDF upload/attach are
            // best-effort (a Fibery hiccup shouldn't block check-in), so fire
            // them off and let them resolve in the background rather than
            // waiting on them before navigating away.
            const emailPromise = fetchWithTimeout(
                `/send-nda-email?${params.toString()}`,
                {
                    method: "POST",
                    body: emailBlob,
                    headers: {
                        "Content-Type": "application/octet-stream",
                    },
                },
                20000,
            );

            updateVisitorNdaSigned(visitorData.id)
                .then((updatedVisitor) => {
                    console.log(
                        "✓ Visitor NDA status updated successfully:",
                        updatedVisitor,
                    );
                })
                .catch((fiberyErr) => {
                    console.error(
                        "✗ Error updating Fibery NDA status:",
                        fiberyErr.message,
                    );
                });

            const uploadParams = new URLSearchParams({
                visitorId: visitorData.id,
            });
            fetchWithTimeout(
                `/upload-nda-to-fibery?${uploadParams.toString()}`,
                {
                    method: "POST",
                    body: pdfBlob,
                    headers: {
                        "Content-Type": "application/octet-stream",
                    },
                },
                20000,
            )
                .then(async (uploadResponse) => {
                    if (!uploadResponse.ok) {
                        const uploadText = await uploadResponse.text();
                        console.warn(
                            "⚠ Failed to upload PDF to Fibery:",
                            uploadText,
                        );
                        return;
                    }
                    const uploadData = await uploadResponse.json();
                    console.log(
                        "✓ NDA PDF uploaded and attached to Fibery successfully",
                        uploadData,
                    );
                })
                .catch((uploadErr) => {
                    console.warn(
                        "⚠ Error uploading PDF to Fibery:",
                        uploadErr.message,
                    );
                });

            const response = await emailPromise;

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.details || "Failed to send email");
            }

            // Navigate to success page
            navigate("/success");
        } catch (err) {
            console.error("Error signing NDA:", err);
            setError("Failed to sign NDA. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="signin-page">
            <header className="signin-header">
                <button className="back-button" onClick={handleBack}>
                    Back
                </button>
                <h1 className="signin-title">
                    {step === 1
                        ? "Visitor Sign In"
                        : "Non-Disclosure Agreement"}
                </h1>
            </header>

            <main className="signin-main">
                {step === 1 ? (
                    <form className="signin-form" onSubmit={handleSubmit}>
                        {error && <div className="error-message">{error}</div>}

                        <div className="form-group">
                            <label htmlFor="firstName">First Name *</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                                placeholder="Enter your first name"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="surname">Surname *</label>
                            <input
                                type="text"
                                id="surname"
                                name="surname"
                                value={formData.surname}
                                onChange={handleChange}
                                required
                                placeholder="Enter your surname"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                placeholder="Enter your email address"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phoneNumber">
                                Phone Number (optional)
                            </label>
                            <input
                                type="tel"
                                id="phoneNumber"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                placeholder="Enter your phone number"
                                disabled={loading}
                            />
                        </div>

                        <button
                            type="submit"
                            className="submit-button"
                            disabled={loading}
                        >
                            {loading ? "Processing..." : "Continue"}
                        </button>
                    </form>
                ) : (
                    <div className="nda-signing-container">
                        <div className="nda-content-wrapper">
                            <div className="nda-document">
                                {NDA_CONTENT.split("\n").map((line, idx) => {
                                    if (
                                        line.startsWith("**") &&
                                        line.endsWith("**")
                                    ) {
                                        return (
                                            <h2
                                                key={idx}
                                                className="nda-section-heading"
                                            >
                                                {line
                                                    .replace(/\*\*/g, "")
                                                    .trim()}
                                            </h2>
                                        );
                                    } else if (line.match(/^\d+\./)) {
                                        return (
                                            <p
                                                key={idx}
                                                className="nda-numbered-section"
                                            >
                                                {line}
                                            </p>
                                        );
                                    } else if (line.trim() === "") {
                                        return null;
                                    } else {
                                        return (
                                            <p key={idx} className="nda-text">
                                                {line}
                                            </p>
                                        );
                                    }
                                })}
                            </div>

                            <div className="nda-signature-form">
                                <h3>Sign Below</h3>

                                {error && (
                                    <div className="error-message">{error}</div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="signature">
                                        Signature *
                                    </label>
                                    <input
                                        type="text"
                                        id="signature"
                                        value={signature}
                                        onChange={(e) => {
                                            setSignature(e.target.value);
                                            setError("");
                                        }}
                                        placeholder="Enter your signature"
                                        disabled={loading}
                                        className="signature-input"
                                    />
                                    <p className="signature-preview">
                                        <span
                                            style={{
                                                fontFamily: "'Caveat', cursive",
                                                fontSize: "28px",
                                                fontWeight: "700",
                                            }}
                                        >
                                            {signature ||
                                                "(Your signature will appear here)"}
                                        </span>
                                    </p>
                                </div>

                                <div className="date-display">
                                    <strong>Date:</strong> {getCurrentDate()}
                                </div>

                                <button
                                    className="sign-button"
                                    onClick={handleNdaSign}
                                    disabled={loading}
                                >
                                    {loading
                                        ? "Sending PDF..."
                                        : "Sign & Complete Check-In"}
                                </button>

                                <p className="form-note">
                                    By clicking the sign button, your signed NDA
                                    will be generated and sent to your email.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default VisitorSignIn;
